import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaderboardConfigPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({
    leaderboard_scope: 'hospital_wide',
    strength_threshold: 80,
    improvement_threshold: 60,
    show_leaderboard: true
  });
  
  useEffect(() => {
    fetchConfig();
  }, []);
  
  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospital/leaderboard/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/hospital/leaderboard/config`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: config
        }
      );
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Leaderboard Configuration</h1>
            <Button onClick={() => navigate('/dashboard')} variant="outline">Back</Button>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg p-6 shadow space-y-6">
          <div>
            <Label>Leaderboard Scope</Label>
            <Select value={config.leaderboard_scope} onValueChange={(val) => setConfig({...config, leaderboard_scope: val})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hospital_wide">Institution-wide</SelectItem>
                <SelectItem value="department_wise">Department-wise</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500 mt-1">Choose whether staff see rankings across the entire institution or within their department</p>
          </div>
          <div>
            <Label>Strength Threshold (%)</Label>
            <Input type="number" value={config.strength_threshold} onChange={(e) => setConfig({...config, strength_threshold: parseFloat(e.target.value)})} min={0} max={100} />
            <p className="text-sm text-gray-500 mt-1">Chapters with scores ≥ this value are marked as strengths</p>
          </div>
          <div>
            <Label>Improvement Threshold (%)</Label>
            <Input type="number" value={config.improvement_threshold} onChange={(e) => setConfig({...config, improvement_threshold: parseFloat(e.target.value)})} min={0} max={100} />
            <p className="text-sm text-gray-500 mt-1">Chapters with scores ≤ this value are marked as needing improvement</p>
          </div>
          <div className="flex items-center space-x-3">
            <Checkbox checked={config.show_leaderboard} onCheckedChange={(checked) => setConfig({...config, show_leaderboard: checked})} />
            <Label>Show Leaderboard to Staff</Label>
          </div>
          <Button onClick={handleSave} className="w-full bg-[#0056B3]">Save Configuration</Button>
        </div>
      </main>
    </div>
  );
};

export default LeaderboardConfigPage;